import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLive } from "@/lib/useLive";
import { cachedGet, overlayAttendance, manualOpenNow, QUEUE_EVENT } from "@/lib/attQueue";
import { formatSessionRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { InvitePicker, AttButtons, PendingSyncBar } from "@/components/SessionAttendanceDialog";
import { Input } from "@/components/ui/input";
import { QrCheckin } from "@/components/QrCheckin";
import { PaidToggle } from "@/components/PaidToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Loader2, UserPlus, MessageCircle, CheckCircle2, Clock, XCircle, QrCode, Ticket, AlertTriangle, Search } from "lucide-react";

const durMin = (s) => (s.ends_at ? Math.round((new Date(s.ends_at) - new Date(s.starts_at)) / 60000) : (s.duration_min || 0));

const WaContact = ({ x }) => {
  const digits = String(x.whatsapp || "").replace(/\D/g, "");
  if (!digits) return null;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp"
        className="grid place-items-center w-7 h-7 rounded-full text-[hsl(145,42%,38%)] hover:bg-[hsl(145,42%,38%)]/10 transition-colors"
        data-testid={`wa-contact-${x.reservation_id}`}>
        <MessageCircle className="w-4 h-4" />
      </a>
      {x.whatsapp}
    </span>
  );
};

const cupoLabel = (a, q) => (q > 1 ? `${a}–${a + q - 1}` : `${a}`);

const AttBadge = ({ x, past }) => {
  const t = `att-badge-${x.reservation_id}`;
  if (x.attendance === "presente") return <Badge className="bg-emerald-700 hover:bg-emerald-700 text-white" data-testid={t}>Presente · {x.checkin_method === "ml" ? "manual" : "QR"}</Badge>;
  if (x.attendance === "ausente" || past) return <Badge variant="outline" className="border-rose-300 text-rose-700" data-testid={t}>Ausente</Badge>;
  if (x.status === "confirmed") return <Badge className="bg-emerald-600/15 text-emerald-800 hover:bg-emerald-600/15" title="Según el alumno" data-testid={t}>Dice que asistirá</Badge>;
  if (x.status === "declined") return <Badge variant="outline" className="border-rose-300 text-rose-700" title="Según el alumno" data-testid={t}>Dice que no asistirá</Badge>;
  return <Badge variant="outline" data-testid={t}>Pendiente</Badge>;
};

const owes = (x) => x.direct && !x.direct_paid && x.direct_price_clp > 0;
const OweBadge = ({ x }) => (owes(x) && x.attendance === "presente"
  ? <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-700" data-testid={`att-owes-${x.reservation_id}`}><AlertTriangle className="w-3.5 h-3.5" />Asistió y debe</span> : null);

function ManualBtn({ x, s, onDone }) {
  if (!manualOpenNow(s)) return null;
  return <AttButtons x={x} s={s} reload={onDone} />;
}

const FILTERS = [
  { id: "all", t: "Todos", f: () => true },
  { id: "tomark", t: "Por marcar", f: (x) => x.attendance === "pendiente" },
  { id: "present", t: "Presentes", f: (x) => x.attendance === "presente" },
  { id: "absent", t: "Ausentes", f: (x) => x.attendance === "ausente" },
  { id: "owing", t: "Pendiente de pago", f: owes },
];
const norm = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function ScanDialog({ s, onClose, onScanned }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[92vh] overflow-y-auto" data-testid="session-scan-dialog">
        <DialogHeader>
          <DialogTitle>Escanear asistencia</DialogTitle>
          <DialogDescription>{formatSessionRange(s.starts_at, durMin(s))}</DialogDescription>
        </DialogHeader>
        <QrCheckin onScanned={onScanned} />
      </DialogContent>
    </Dialog>
  );
}

const attKey = (x, marking) => (marking
  ? (x.attendance === "presente" ? "si" : x.attendance === "ausente" ? "no" : "pend")
  : (x.status === "confirmed" ? "si" : x.status === "declined" ? "no" : "pend"));

function SessionSummary({ students, marking }) {
  const c = students.reduce((a, x) => ({ ...a, [attKey(x, marking)]: a[attKey(x, marking)] + 1 }), { si: 0, pend: 0, no: 0 });
  const debt = students.filter(owes).length;
  const items = [
    { k: "si", n: c.si, label: marking ? "Presentes" : "Asistirán", Icon: CheckCircle2 },
    { k: "pend", n: c.pend, label: marking ? "Por marcar" : "Pendientes", Icon: Clock },
    { k: "no", n: c.no, label: marking ? "Ausentes" : "No asistirán", Icon: XCircle },
    { k: "debt", n: debt, label: "Pendiente de pago", Icon: AlertTriangle },
  ];
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-1.5" data-testid="session-summary">
      {items.map(({ k, n, label, Icon }) => (
        <span key={k} title={label} data-testid={`session-summary-${k}`}
          className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium">
          <Icon className="w-3.5 h-3.5" /><strong className="font-bold">{n}</strong>{label}
        </span>
      ))}
    </div>
  );
}

function SessionNav({ idx, total, s, onMove }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl bg-primary text-primary-foreground p-2 shadow-[0_8px_24px_-10px_hsl(var(--primary)/0.6)]" data-testid="session-nav">
      <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/15 hover:text-primary-foreground disabled:opacity-30" disabled={idx <= 0} onClick={() => onMove(-1)} aria-label="Sesión anterior" data-testid="session-nav-prev">
        <ChevronLeft className="w-5 h-5" />
      </Button>
      <div className="text-center min-w-0">
        <p className="font-display font-bold" data-testid="session-nav-number">Sesión {idx + 1} de {total}</p>
        <p className="text-xs sm:text-sm text-primary-foreground/85" data-testid="session-nav-date">{formatSessionRange(s.starts_at, durMin(s))}</p>
        <SessionSummary students={s.students} marking={s.past || s.manual_open} />
        {s.past && (
          <p className="mt-1.5 text-xs font-semibold" data-testid="session-present-count">{s.present} de {s.expected} presentes</p>
        )}
      </div>
      <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/15 hover:text-primary-foreground disabled:opacity-30" disabled={idx >= total - 1} onClick={() => onMove(1)} aria-label="Sesión siguiente" data-testid="session-nav-next">
        <ChevronRight className="w-5 h-5" />
      </Button>
    </div>
  );
}

const CanjeBadge = ({ x }) => (x.canje ? (
  <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-semibold align-middle" data-testid={`att-canje-${x.reservation_id}`}>
    <Ticket className="w-3 h-3" />Canje{x.canje_by === "invitacion" ? " · invitación" : ""}
  </span>
) : null);

function MobileList({ students, empties, s, reload }) {
  return (
    <div className="sm:hidden divide-y rounded-xl border" data-testid="confirmed-mobile-list">
      {students.map((x) => (
        <div key={x.reservation_id} className={`p-3 space-y-1.5 border-l-4 ${x.attendance === "presente" ? (owes(x) ? "border-l-amber-400 bg-amber-50/50" : "border-l-emerald-500") : x.attendance === "ausente" ? "border-l-rose-400" : "border-l-transparent"}`} data-testid={`confirmed-card-${x.reservation_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium break-all">{x.name || "—"}<CanjeBadge x={x} /></p>
              <p className="text-xs text-muted-foreground break-all">{x.email}</p>
            </div>
            <AttBadge x={x} past={s.past && !s.manual_open} />
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
            <span>Cupo {x.cupo}</span>{x.whatsapp && <><span>·</span><WaContact x={x} /></>}<span>·</span><span>{x.quantity} cupo(s)</span>
          </div>
          <div className="flex flex-wrap items-center gap-2"><ManualBtn x={x} s={s} onDone={reload} /><PaidToggle x={x} onChange={reload} /><OweBadge x={x} /></div>
        </div>
      ))}
      {empties.map((n) => (
        <div key={`e-${n}`} className="p-3 text-xs text-muted-foreground/50" data-testid={`empty-card-${n}`}>Cupo {n}</div>
      ))}
    </div>
  );
}

export function ConfirmedBySession({ workshopId }) {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picking, setPicking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("all");
  const load = useCallback(() => cachedGet(`/business/workshops/${workshopId}/attendance`).then((r) => {
    const d = overlayAttendance(r.data);
    setData(d);
    return d;
  }), [workshopId]);
  useEffect(() => { const h = () => load().catch(() => {}); window.addEventListener(QUEUE_EVENT, h); return () => window.removeEventListener(QUEUE_EVENT, h); }, [load]);
  useLive(load, 5000);

  useEffect(() => {
    load().then((d) => {
      const live = d.sessions.map((s) => s.scan_open && s.past).lastIndexOf(true);
      const next = d.sessions.findIndex((s) => !s.past);
      setIdx(live !== -1 ? live : next === -1 ? Math.max(d.sessions.length - 1, 0) : next);
    });
  }, [load]);

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-8" />;
  if (data.sessions.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">Este taller no tiene sesiones.</p>;

  const s = { ...data.sessions[idx], duration_min: data.workshop.duration_min };
  const used = s.students.filter((x) => x.canje_by !== "invitacion" || !x.canje).reduce((a, x) => a + x.quantity, 0);
  const empty = Math.max((data.workshop.capacity || 0) - used, 0);
  let next = 1;
  const allRows = s.students.map((x) => { const r = { ...x, cupo: cupoLabel(next, x.quantity) }; next += x.quantity; return r; });
  const filtering = flt !== "all" || q.trim();
  const rows = allRows.filter((x) => FILTERS.find((f) => f.id === flt).f(x) && (!q.trim() || norm(`${x.name} ${x.email}`).includes(norm(q.trim()))));
  const empties = filtering ? [] : [...Array(empty)].map((_, i) => next + i);
  const move = (d) => { setPicking(false); setIdx((i) => i + d); };

  return (
    <div className="space-y-3" data-testid="confirmed-by-session">
      <SessionNav idx={idx} total={data.sessions.length} s={s} onMove={move} />
      <PendingSyncBar />
      {s.canjes > 0 && (
        <p className="text-xs rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 flex items-center gap-1.5" data-testid={`att-canjes-${s.id}`}>
          <Ticket className="w-3.5 h-3.5 text-primary" /><strong>{s.canjes}</strong> {s.canjes === 1 ? "alumno tomó" : "alumnos tomaron"} esta sesión canjeando un crédito.
        </p>
      )}
      {s.scan_open && (
        <Button className="w-full" onClick={() => setScanning(true)} data-testid="session-scan-btn"><QrCode className="w-4 h-4 mr-1.5" />Escanear QR de asistencia</Button>
      )}
      {scanning && <ScanDialog s={s} onClose={() => setScanning(false)} onScanned={load} />}
      {s.manual_open && <p className="text-[11px] text-muted-foreground" data-testid="att-priority-note">Tu marca (QR o manual) es la asistencia oficial y reemplaza lo que haya indicado el alumno.</p>}
      {s.students.length > 0 && (
        <div className="space-y-2" data-testid="att-tools">
          <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8 h-9" placeholder="Buscar alumno por nombre o correo" value={q} onChange={(e) => setQ(e.target.value)} data-testid="att-search" /></div>
          <div className="flex gap-1.5 flex-wrap">
            {FILTERS.map((f) => <button key={f.id} type="button" onClick={() => setFlt(f.id)} className={`h-7 px-3 rounded-full text-xs font-semibold border transition-colors ${flt === f.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`} data-testid={`att-filter-${f.id}`}>{f.t} <span className="opacity-70">{allRows.filter(f.f).length}</span></button>)}
          </div>
        </div>
      )}
      {filtering && rows.length === 0 && <p className="text-sm text-center text-muted-foreground py-4" data-testid="att-no-match">Ningún alumno coincide.</p>}
      <MobileList students={rows} empties={empties} s={s} reload={load} />
      <div className="hidden sm:block overflow-x-auto">
      <Table className="min-w-[720px]">
        <TableHeader><TableRow>
          <TableHead className="w-16">Cupo</TableHead><TableHead>Alumno</TableHead><TableHead>Contacto</TableHead>
          <TableHead className="text-center">Cupos</TableHead><TableHead>Pago</TableHead><TableHead>Asistencia</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((x) => (
            <TableRow key={x.reservation_id} className={x.attendance === "presente" && owes(x) ? "bg-amber-50/60" : x.attendance === "ausente" ? "bg-rose-50/40" : ""} data-testid={`confirmed-row-${x.reservation_id}`}>
              <TableCell className="font-medium" data-testid={`cupo-${x.reservation_id}`}>{x.cupo}</TableCell>
              <TableCell className="min-w-[150px]">
                <p className="font-medium">{x.name || "—"}<CanjeBadge x={x} /></p>
                <p className="text-xs text-muted-foreground break-all">{x.email}</p>
              </TableCell>
              <TableCell className="text-sm"><WaContact x={x} /></TableCell>
              <TableCell className="text-center">{x.quantity}</TableCell>
              <TableCell><div className="flex flex-col items-start gap-1"><PaidToggle x={x} onChange={load} /><OweBadge x={x} /></div></TableCell>
              <TableCell><div className="flex flex-wrap items-center gap-1.5"><AttBadge x={x} past={s.past && !s.manual_open} /><ManualBtn x={x} s={s} onDone={load} /></div></TableCell>
            </TableRow>
          ))}
          {empties.map((n) => (
            <TableRow key={`empty-${n}`} className="text-muted-foreground/50" data-testid={`empty-row-${n}`}>
              <TableCell>{n}</TableCell><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      {!s.past && s.open_spots > 0 && (picking
        ? <InvitePicker s={s} onDone={() => { setPicking(false); load(); }} />
        : <Button variant="outline" className="w-full" onClick={() => setPicking(true)} data-testid="open-invite-picker">
            <UserPlus className="w-4 h-4 mr-1.5" />Invitar a {s.open_spots} cupo(s) liberado(s)
          </Button>)}
    </div>
  );
}
